import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { X, Calendar, Check } from "lucide-react";

/**
 * Dialog to log a session (visit) on a client's profile.
 * Practitioners + admins can record a completed session, choose service,
 * pick a date+time (defaults to "now"), and optionally deduct from an active
 * package the client already owns.
 */
export default function LogSessionDialog({ clientId, packages = [], onClose, onSaved }) {
  const [services, setServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [practitionerId, setPractitionerId] = useState("");
  const [when, setWhen] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    // Format for datetime-local input (browser local time)
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [outcome, setOutcome] = useState("completed");
  const [notes, setNotes] = useState("");
  const [packageId, setPackageId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/services").then(({ data }) => {
      // Only show bookable + non-walk-in for normal session logging
      const list = data.filter((s) => s.active !== false && !s.is_add_on);
      setServices(list);
    });
    api.get("/practitioners").then(({ data }) => setPractitioners(data.filter((p) => p.active !== false)));
  }, []);

  // Eligible packages — those still with sessions remaining + eligible for the chosen service
  const activePackages = packages.filter((p) => {
    const remaining = (p.remaining ?? (p.original_quantity - p.used_quantity));
    if (remaining <= 0) return false;
    if (p.status === "expired") return false;
    if (serviceId && p.package_type?.eligible_service_ids?.length) {
      return p.package_type.eligible_service_ids.includes(serviceId);
    }
    return true;
  });

  const selectedPackage = activePackages.find((p) => p.id === packageId);
  const selectedService = services.find((s) => s.id === serviceId);

  const save = async () => {
    setError("");
    if (!serviceId) { setError("Pick a service."); return; }
    if (!when) { setError("Pick a date/time."); return; }
    setBusy(true);
    try {
      // datetime-local is a local-time string; convert to ISO with the browser's offset.
      const iso = new Date(when).toISOString();
      await api.post(`/clients/${clientId}/log-session`, {
        service_id: serviceId,
        practitioner_id: practitionerId || null,
        scheduled_start: iso,
        outcome,
        notes: notes.trim(),
        use_package_id: packageId || null,
      });
      await onSaved();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" data-testid="log-session-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
          <h2 className="font-display text-xl text-charcoal flex items-center gap-2">
            <Calendar className="w-5 h-5 text-sage" /> Log a session
          </h2>
          <button onClick={onClose} className="p-1 text-slate hover:text-charcoal" data-testid="close-log-session"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="block text-xs font-ui text-slate mb-1">Service *</span>
            <select className={inputCls} value={serviceId} onChange={(e) => { setServiceId(e.target.value); setPackageId(""); }} data-testid="log-session-service">
              <option value="">Pick a service…</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>{s.name} · {s.duration_minutes}min</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs font-ui text-slate mb-1">Practitioner</span>
            <select className={inputCls} value={practitionerId} onChange={(e) => setPractitionerId(e.target.value)} data-testid="log-session-practitioner">
              <option value="">Auto (from day rule)</option>
              {practitioners.map((p) => (
                <option key={p.id} value={p.id}>{p.display_name}</option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-ui text-slate mb-1">When *</span>
              <input type="datetime-local" className={inputCls} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="log-session-when" />
            </label>
            <label className="block">
              <span className="block text-xs font-ui text-slate mb-1">Outcome</span>
              <select className={inputCls} value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="log-session-outcome">
                <option value="completed">Completed</option>
                <option value="no_show">No-show</option>
              </select>
            </label>
          </div>

          {/* Package selector — only shown for completed visits with eligible packages */}
          {outcome === "completed" && activePackages.length > 0 && (
            <div>
              <span className="block text-xs font-ui text-slate mb-1.5">Deduct from package?</span>
              <div className="space-y-1.5">
                <label className="flex items-center gap-2.5 px-3 py-2 bg-cream/40 border border-hairline rounded-md cursor-pointer hover:bg-cream/60">
                  <input
                    type="radio"
                    name="pkg"
                    checked={packageId === ""}
                    onChange={() => setPackageId("")}
                    className="text-forest focus:ring-sage"
                    data-testid="log-session-pkg-none"
                  />
                  <span className="text-sm font-ui text-charcoal">No — charge as a regular visit</span>
                </label>
                {activePackages.map((p) => {
                  const remaining = p.remaining ?? (p.original_quantity - p.used_quantity);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center justify-between gap-3 px-3 py-2 border rounded-md cursor-pointer transition ${packageId === p.id ? "border-forest bg-sage/10" : "border-hairline hover:bg-cream/40"}`}
                      data-testid={`log-session-pkg-${p.id}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <input
                          type="radio"
                          name="pkg"
                          checked={packageId === p.id}
                          onChange={() => setPackageId(p.id)}
                          className="text-forest focus:ring-sage shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-ui text-charcoal truncate">{p.package_type?.name || "Package"}</p>
                          <p className="text-[10px] font-mono text-slate uppercase tracking-wider">{remaining} of {p.original_quantity} sessions remaining</p>
                        </div>
                      </div>
                      <span className="font-mono text-xs text-charcoal shrink-0">−1</span>
                    </label>
                  );
                })}
              </div>
              {selectedPackage && selectedService && (
                <p className="text-xs font-ui text-slate mt-2 inline-flex items-center gap-1">
                  <Check className="w-3 h-3 text-status-success" />
                  After this visit: {(selectedPackage.remaining ?? selectedPackage.original_quantity - selectedPackage.used_quantity) - 1} session{((selectedPackage.remaining ?? selectedPackage.original_quantity - selectedPackage.used_quantity) - 1) === 1 ? "" : "s"} remaining
                </p>
              )}
            </div>
          )}

          {outcome === "completed" && packages.length > 0 && activePackages.length === 0 && (
            <p className="text-xs font-ui text-slate italic">No active packages eligible for this service.</p>
          )}

          <label className="block">
            <span className="block text-xs font-ui text-slate mb-1">Session notes</span>
            <textarea rows={3} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Quick visit notes — practitioner reference only." data-testid="log-session-notes" />
          </label>

          {error && <div className="px-3 py-2 bg-status-danger-bg/40 border border-status-danger/30 rounded-md text-xs font-ui text-status-danger" data-testid="log-session-error">{error}</div>}
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-hairline bg-white text-charcoal text-sm font-ui rounded-md hover:bg-cream">Cancel</button>
          <button onClick={save} disabled={busy || !serviceId} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md disabled:opacity-40" data-testid="log-session-save">
            {busy ? "Saving…" : "Log session"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30";
